import type { FC } from 'react'
import { useCallback } from 'react'

import { ImagePlaceholder } from '@liveHelper/assets/ImagePlaceholder'
import {
  FileNameText,
  FileSizeText,
  MessagePreviewAvatar,
  MessagePreviewBadge,
  MessagePreviewCloseButton,
  MessagePreviewContainer,
  MessagePreviewContainerStack,
  MessagePreviewContent,
  MessagePreviewGroup,
} from '@liveHelper/components/MessagePreview/MessagePreview.styles'
import { useMessagePreviewContext } from '@liveHelper/components/MessagePreviewProvider'
import { AgentImage } from '@liveHelper/components/shared/styles'
import {
  actionsSelector,
  uiStateSelector,
  useStore,
} from '@liveHelper/models/store'
import { useTrackClickWidget } from '@liveHelper/tracking/useLiveHelperTracking'
import { Close } from '@oneToOne/shared/assets/Close'
import { DocumentAttachment } from '@oneToOne/shared/assets/DocumentAttachment'
import type { MessageAttachment } from '@oneToOne/shared/components/chat/types'
import {
  DocumentIconContainer,
  PlayIconContainer,
} from '@oneToOne/shared/components/shared.styles'
import {
  categorizeFileType,
  formatFileSize,
  truncateFileName,
} from '@oneToOne/shared/utils/file'

import { Play } from '@core/assets/Play'
import { useMeasure } from '@core/hooks/useMeasure'
import { useContextTranslation } from '@core/i18n'

interface Props {
  avatarSrc?: string
  content: string
  attachment: MessageAttachment | undefined
  number: number
}

/**
 * The message preview component on the widget
 */
export const MessagePreview: FC<Props> = ({
  avatarSrc,
  content,
  attachment,
  number,
}) => {
  const uiState = useStore(uiStateSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)

  const trackClickWidget = useTrackClickWidget()

  const { dismissMessagePreview } = useMessagePreviewContext()

  const handleClick = useCallback(() => {
    trackClickWidget({
      click_target: 'message_preview',
      last_unread_message: content,
    })
    sendUIStateTransitionEvent('START_CHAT')
  }, [sendUIStateTransitionEvent, trackClickWidget, content])

  const handleClose = useCallback(() => {
    dismissMessagePreview()

    if (uiState === 'idle_expanded') {
      sendUIStateTransitionEvent('MINIMIZE')
    }
  }, [dismissMessagePreview, sendUIStateTransitionEvent, uiState])

  const { t } = useContextTranslation()

  const {
    ref,
    rect: { width, height },
  } = useMeasure<HTMLDivElement>()

  const renderContent = () => {
    if (attachment) {
      const renderAttachmentInfo = () => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <FileNameText>
              {truncateFileName(attachment.filename ?? '', 15)}
            </FileNameText>
            <FileSizeText>
              {formatFileSize(attachment.file_size ?? 0)}
            </FileSizeText>
          </div>
        )
      }

      const fileType = categorizeFileType(attachment?.file_mime_type || '')

      switch (fileType) {
        case 'image':
          return (
            <>
              <DocumentIconContainer>
                <ImagePlaceholder
                  width="18px"
                  height="14px"
                  fill="var(--blk2)"
                  label="image placeholder"
                />
              </DocumentIconContainer>
              {renderAttachmentInfo()}
            </>
          )
        case 'media':
          return (
            <>
              <PlayIconContainer>
                <Play fill="var(--white)" label="play" />
              </PlayIconContainer>
              {renderAttachmentInfo()}
            </>
          )
        default:
          return (
            <>
              <DocumentIconContainer>
                <DocumentAttachment
                  width="22px"
                  height="19px"
                  label="document"
                />
              </DocumentIconContainer>
              {renderAttachmentInfo()}
            </>
          )
      }
    }

    return (
      <>
        {avatarSrc ? (
          <MessagePreviewAvatar>
            <AgentImage src={avatarSrc} alt={t(`oneToOne:Agent picture`)} />
          </MessagePreviewAvatar>
        ) : null}
        <MessagePreviewContent key={content}>{content}</MessagePreviewContent>
      </>
    )
  }

  return (
    <MessagePreviewGroup onClick={handleClick}>
      <MessagePreviewContainer ref={ref}>
        {number ? (
          <MessagePreviewBadge>
            <span key={number}>{number}</span>
          </MessagePreviewBadge>
        ) : null}
        <MessagePreviewCloseButton
          aria-label={t(`oneToOne:Dismiss notification`)}
          actionType="neutral"
          buttonSize="xs"
          shape="circle"
          onClick={(e) => {
            e.stopPropagation()
            handleClose()
          }}
          icon={<Close height="8px" width="8px" label="close" />}
        />
        {renderContent()}
      </MessagePreviewContainer>
      {number > 1 ? (
        <MessagePreviewContainerStack width={width ?? 0} height={height ?? 0} />
      ) : null}
    </MessagePreviewGroup>
  )
}
